import React, { FC, ReactNode, RefObject } from 'react';
import styled from 'styled-components';
import { Placement } from '@popperjs/core';
import { THEME_PRIMARY, themeColor } from '../../styles/basics/colors';
import { LENDING_MODEL_OCOU, LENDING_MODEL_SIM, UNLIMITED_USAGE_LIMIT } from '../../store/constants';
import { StockDetails } from '../../store/api';
import { Popup, PopupTheme } from '../components/Popup/Popup';
import { useTranslation } from 'react-i18next';
import { mapLoanFormat } from '../../../models/LoanFormat';
import { LoanFormatState } from '../../app-store/loanFormat/loanFormatReducer';

const StCampaignStockText = styled.td`
    color: ${themeColor(THEME_PRIMARY)};
    font-weight: bold;
`;

const StCampaignLabel = styled.b`
    color: ${themeColor(THEME_PRIMARY)};
`;

StCampaignStockText.displayName = 'ProductCopyStock__StCampaignStockText';

type Props = {
    readonly loanFormat: LoanFormatState;
    readonly stockDetails?: StockDetails;
    readonly copyStockAmount?: number;
    readonly isLibraryManagedProduct?: boolean;
    readonly label: string;
    readonly placement: Placement;
    readonly labelAppendix?: ReactNode;
    readonly iconClassName?: string;
};

type ProductCopyStockDropdownProps = {
    readonly placement: Placement;
    readonly visible: boolean;
    readonly popoverRef: RefObject<HTMLElement>;
    readonly stockDetails?: StockDetails;
    readonly loanFormat: LoanFormatState;
};
export const ProductCopyStockDropdown: FC<ProductCopyStockDropdownProps> = ({
    placement,
    visible,
    popoverRef,
    stockDetails,
    loanFormat
}) => {
    return (
        <Popup
            target={popoverRef}
            visible={visible}
            placement={placement}
            displacement={-24}
            theme={PopupTheme.CUSTOM}
            popupClassName="connect-popover"
        >
            <StockDetailsList stockDetails={stockDetails} loanFormat={loanFormat} />
        </Popup>
    );
};

export const collectionOrder = (a: any, b: any) => {
    const valueA = a.collectionName;
    const valueB = b.collectionName;

    if (valueA.startsWith('Shared') && !valueB.startsWith('Shared')) {
        return -1;
    }
    if (!valueA.startsWith('Shared') && valueB.startsWith('Shared')) {
        return 1;
    }
    if (valueA < valueB) {
        return -1;
    }
    if (valueA > valueB) {
        return 1;
    }
    return 0;
};

export const mapStockDetailsData = (stockDetails: StockDetails): StockDetails => {
    const combinedStockDetails = {};
    Object.entries(stockDetails).forEach(stockDetailsItem => {
        const [spocId, { usageConditions }] = stockDetailsItem;

        combinedStockDetails[spocId] = {
            spocName: stockDetails[spocId].spocName,
            collectionName: stockDetails[spocId].collectionName,
            usageConditions: {}
        };

        Object.entries(usageConditions).forEach(([key, stocks]) => {
            const parsedLendingModelKey = key.split('-');
            const muc = parsedLendingModelKey[0];
            const usageLimit = parseInt(parsedLendingModelKey[1]);

            const label = `${muc}-${usageLimit}`;

            if (combinedStockDetails[spocId].usageConditions.hasOwnProperty(label)) {
                Object.entries(stocks).forEach(([stock, value]) => {
                    combinedStockDetails[spocId].usageConditions[label][stock] += value;
                });
            } else {
                combinedStockDetails[spocId].usageConditions[label] = { ...stocks };
            }
        });
    });
    return combinedStockDetails;
};

const StockDetailsList: FC<Pick<Props, 'loanFormat' | 'stockDetails'>> = ({ loanFormat, stockDetails }) => {
    const { t } = useTranslation();
    if (!stockDetails) {
        return null;
    }

    const mappedLoanFormat = mapLoanFormat(loanFormat.requestKey);

    const stockDetailsCollections = Object.entries(mapStockDetailsData(stockDetails))
        .map(([spocId, detail]) => ({ spocId, ...detail }))
        .sort(collectionOrder);

    return (
        <>
            {stockDetailsCollections.map(({ spocId, spocName, collectionName, usageConditions }) => {
                let campaignCount = 0;
                const localisedCollectionName = spocId.startsWith('SITE')
                    ? t('components.stockDetails.individualCollection', { spocName, defaultValue: collectionName })
                    : t('components.stockDetails.sharedCollection', { spocName, defaultValue: collectionName });
                return (
                    <section className="bordered-section" key={collectionName}>
                        <span className="title">{localisedCollectionName}</span>
                        <table className="left-aligned-data" data-testid="stock-info-dropdown">
                            <tbody>
                                {Object.entries(usageConditions).map(
                                    ([lendingModelKey, { overallStock = 0, campaignStock = 0 }]) => {
                                        const stockCount = overallStock - campaignStock;
                                        const copyStockLabel = t(
                                            `loanFormat.plain.${mappedLoanFormat?.toLowerCase()}`,
                                            {
                                                count: stockCount
                                            }
                                        );
                                        campaignCount += campaignStock;

                                        if (stockCount === 0) {
                                            return null;
                                        }

                                        const parsedLendingModelKey = lendingModelKey.split('-');
                                        const lendingModel = parsedLendingModelKey[0];
                                        const usageLimit = parseInt(parsedLendingModelKey[1]);

                                        const isLendingModelSim = lendingModel === LENDING_MODEL_SIM;
                                        const isLendingModelOCOU = lendingModel === LENDING_MODEL_OCOU;

                                        const unlimited = isLendingModelSim && usageLimit === UNLIMITED_USAGE_LIMIT;

                                        const labelText = unlimited
                                            ? t('shop.subscriptions.unlimited')
                                            : isLendingModelOCOU
                                            ? t('lendingModel.ocou')
                                            : `${t(`lendingModel.${lendingModel.toLowerCase()}`)}-${usageLimit}`;

                                        return (
                                            <tr
                                                key={`${lendingModelKey}-${overallStock}-${campaignStock}`}
                                                className="emphasis"
                                            >
                                                <td>
                                                    <span>{stockCount}</span>
                                                </td>
                                                <td>
                                                    <b>
                                                        {copyStockLabel} - {labelText}
                                                    </b>
                                                </td>
                                            </tr>
                                        );
                                    }
                                )}

                                {campaignCount > 0 ? (
                                    <tr className="mb-0">
                                        <td>{campaignCount}</td>
                                        <td>
                                            <StCampaignLabel>
                                                {t(`campaignStock.${mappedLoanFormat?.toLowerCase()}`, {
                                                    count: campaignCount
                                                })}
                                            </StCampaignLabel>
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </section>
                );
            })}
        </>
    );
};
